<template>
  <div>
    <div v-if="selectedItems.length > 0" class="mb-2">
      <b-badge
        v-for="(item, idx) in selectedItems"
        :key="idx"
        variant="info"
        class="mr-1 mb-1"
        pill
      >
        {{ formatLabel(item) }}
        <b-button
          size="sm"
          variant="link"
          class="p-0 ml-1 text-white"
          @click="removeItem(idx)"
        >
          &times;
        </b-button>
      </b-badge>
    </div>
    <small class="text-muted d-block mb-1"
      >ไม่เลือก = ใช้ได้กับทุกรายการรักษา</small
    >
    <b-input-group>
      <multiselect
        class="col p-0"
        v-model="selected"
        :options="groupedOptions"
        :multiple="true"
        :close-on-select="false"
        group-values="libs"
        group-label="category"
        :group-select="true"
        placeholder="ค้นหารายการรักษา / ยา / สินค้า"
        :custom-label="customLabel"
        track-by="id"
        selectLabel=""
        selectedLabel=""
        deselectLabel=""
        @input="onSelectionChange"
      >
        <template #option="props">
          <span v-if="props.option.$isLabel">
            {{ props.option.$groupLabel }}
          </span>
        </template>
        <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
      </multiselect>
      <b-input-group-append>
        <b-button variant="outline-secondary" @click="showBrowseModal">
          <i class="fas fa-list"></i>
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <!-- Browse Modal -->
    <b-modal
      ref="browseModal"
      title="เลือกรายการรักษา / ยา / สินค้า"
      size="lg"
      ok-title="ตกลง"
      cancel-title="ยกเลิก"
      @ok="applyBrowseSelection"
      @show="initBrowseSelection"
      modal-class="picker-modal"
    >
      <div class="picker-search mb-2">
        <i class="fas fa-search picker-search-icon"></i>
        <b-form-input
          v-model="browseFilter"
          type="search"
          placeholder="ค้นหารายการรักษา / ยา / สินค้า..."
          class="picker-search-input"
          autofocus
        />
      </div>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <b-badge variant="primary" pill class="px-2 py-1">
            <i class="fas fa-check mr-1"></i>{{ browseSelected.length }}
          </b-badge>
          <small class="text-muted ml-1">รายการที่เลือก</small>
        </div>
        <div>
          <b-button size="sm" variant="outline-primary" @click="selectAllTreatments" class="mr-1 btn-picker">
            <i class="fas fa-check-double mr-1"></i>เลือกทั้งหมด
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="deselectAllTreatments" class="btn-picker">
            <i class="fas fa-times mr-1"></i>ล้าง
          </b-button>
        </div>
      </div>
      <div class="picker-list-container">
        <div v-if="filteredTreatCateList.length === 0 && filteredTreatList.length === 0 && filteredItemList.length === 0" class="text-center py-4 text-muted">
          <i class="fas fa-search fa-2x mb-2 d-block"></i>
          <span>ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
        </div>
        <!-- หมวดรักษา -->
        <div v-if="filteredTreatCateList.length > 0" class="picker-category-section">
          <div class="picker-category-header header-purple">
            <div>
              <i class="fas fa-folder mr-1"></i> หมวดรักษา
              <b-badge variant="light" pill class="ml-1">{{ filteredTreatCateList.length }}</b-badge>
            </div>
            <div class="picker-category-actions">
              <b-link class="picker-action-link" @click="selectCategory('treatCate')">เลือกทั้งหมด</b-link>
              <b-link class="picker-action-link text-secondary" @click="deselectCategory('treatCate')">ยกเลิก</b-link>
            </div>
          </div>
          <div class="picker-items">
            <b-form-checkbox
              v-for="item in filteredTreatCateList"
              :key="'cate-' + item.id"
              v-model="browseSelected"
              :value="{ type: 'treatCate', id: item.id, nameTh: item.nameTh }"
              class="picker-item"
            >
              <b-badge variant="light" class="mr-1 text-purple" style="font-size: 0.7rem">หมวด</b-badge>
              {{ item.nameTh }}
            </b-form-checkbox>
          </div>
        </div>
        <!-- รายการรักษา -->
        <div v-if="filteredTreatList.length > 0" class="picker-category-section">
          <div class="picker-category-header header-blue">
            <div>
              <i class="fas fa-tooth mr-1"></i> รายการรักษา
              <b-badge variant="light" pill class="ml-1">{{ filteredTreatList.length }}</b-badge>
            </div>
            <div class="picker-category-actions">
              <b-link class="picker-action-link" @click="selectCategory('treat')">เลือกทั้งหมด</b-link>
              <b-link class="picker-action-link text-secondary" @click="deselectCategory('treat')">ยกเลิก</b-link>
            </div>
          </div>
          <div class="picker-items">
            <b-form-checkbox
              v-for="item in filteredTreatList"
              :key="'treat-' + item.id"
              v-model="browseSelected"
              :value="{ type: 'treat', id: item.id, nameTh: item.nameTh, code: item.code }"
              class="picker-item"
            >
              <code v-if="item.code" class="picker-code mr-1">{{ item.code }}</code>
              {{ item.nameTh }}
            </b-form-checkbox>
          </div>
        </div>
        <!-- ยา / สินค้า -->
        <div v-if="filteredItemList.length > 0" class="picker-category-section">
          <div class="picker-category-header header-green">
            <div>
              <i class="fas fa-pills mr-1"></i> ยา / สินค้า
              <b-badge variant="light" pill class="ml-1">{{ filteredItemList.length }}</b-badge>
            </div>
            <div class="picker-category-actions">
              <b-link class="picker-action-link" @click="selectCategory('item')">เลือกทั้งหมด</b-link>
              <b-link class="picker-action-link text-secondary" @click="deselectCategory('item')">ยกเลิก</b-link>
            </div>
          </div>
          <div class="picker-items">
            <b-form-checkbox
              v-for="item in filteredItemList"
              :key="'item-' + item.id"
              v-model="browseSelected"
              :value="{ type: 'item', id: item.id, nameTh: item.nameTh, code: item.code }"
              class="picker-item"
            >
              <code v-if="item.code" class="picker-code mr-1">{{ item.code }}</code>
              {{ item.nameTh }}
            </b-form-checkbox>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Multiselect from "vue-multiselect";

export default {
  name: "TreatmentPicker",
  components: { Multiselect },
  props: {
    value: { type: Array, default: () => [] },
    clinicUrl: { type: String, required: true },
    branchUrl: { type: String, required: true },
  },
  data() {
    return {
      selected: [],
      selectedItems: [],
      browseFilter: "",
      browseSelected: [],
    };
  },
  computed: {
    ...mapGetters("moduleAppointment", [
      "getTreatList",
      "getTreatCateList",
      "getItemList",
      "getGroupTxListNoFav",
      "getGroupedItemListNoFav",
    ]),
    groupedOptions() {
      const opts = [];
      if (this.getTreatCateList && this.getTreatCateList.length > 0) {
        opts.push({
          category: "หมวดรักษา",
          libs: this.getTreatCateList.map(c => ({
            ...c,
            _type: "treatCate",
          })),
        });
      }
      // Flatten treat groups
      const txGroups = this.getGroupTxListNoFav || [];
      txGroups.forEach(g => {
        opts.push({
          category: `รักษา - ${g.category}`,
          libs: (g.libs || []).map(t => ({ ...t, _type: "treat" })),
        });
      });
      // Flatten item groups
      const itemGroups = this.getGroupedItemListNoFav || [];
      itemGroups.forEach(g => {
        opts.push({
          category: `ยา/สินค้า - ${g.category}`,
          libs: (g.libs || []).map(i => ({ ...i, _type: "item" })),
        });
      });
      return opts;
    },
    filteredTreatCateList() {
      if (!this.browseFilter) return this.getTreatCateList || [];
      const q = this.browseFilter.toLowerCase();
      return (this.getTreatCateList || []).filter(i =>
        i.nameTh?.toLowerCase().includes(q)
      );
    },
    filteredTreatList() {
      if (!this.browseFilter) return this.getTreatList || [];
      const q = this.browseFilter.toLowerCase();
      return (this.getTreatList || []).filter(
        i =>
          i.nameTh?.toLowerCase().includes(q) ||
          i.code?.toLowerCase().includes(q)
      );
    },
    filteredItemList() {
      if (!this.browseFilter) return this.getItemList || [];
      const q = this.browseFilter.toLowerCase();
      return (this.getItemList || []).filter(
        i =>
          i.nameTh?.toLowerCase().includes(q) ||
          i.code?.toLowerCase().includes(q)
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0 && this.selectedItems.length === 0) {
          this.selectedItems = [...val];
          this.syncMultiselectFromItems();
        }
      },
    },
  },
  async mounted() {
    await this.fetchMasterData();
    this.syncMultiselectFromItems();
  },
  methods: {
    ...mapActions("moduleAppointment", [
      "fetchTreatList",
      "fetchTreatCateList",
      "fetchItemList",
    ]),
    async fetchMasterData() {
      try {
        await Promise.all([
          this.fetchTreatList({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
          }),
          this.fetchTreatCateList({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
          }),
          this.fetchItemList({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
          }),
        ]);
      } catch {
        // Master data fetch failed - pickers will be empty
      }
    },
    customLabel(option) {
      const prefix = option.code ? `[${option.code}] ` : "";
      if (option._type === "treatCate") return `[หมวด] ${option.nameTh}`;
      return `${prefix}${option.nameTh}`;
    },
    formatLabel(item) {
      if (item.treatCateId) {
        const cate = (this.getTreatCateList || []).find(
          c => c.id === item.treatCateId
        );
        return cate ? `[หมวด] ${cate.nameTh}` : `หมวด #${item.treatCateId}`;
      }
      if (item.treatId) {
        const treat = (this.getTreatList || []).find(
          t => t.id === item.treatId
        );
        return treat ? treat.nameTh : `รักษา #${item.treatId}`;
      }
      if (item.itemId) {
        const itm = (this.getItemList || []).find(i => i.id === item.itemId);
        return itm ? `[สินค้า] ${itm.nameTh}` : `สินค้า #${item.itemId}`;
      }
      return "";
    },
    onSelectionChange(newVal) {
      this.selectedItems = (newVal || []).map(opt => ({
        treatId: opt._type === "treat" ? opt.id : null,
        treatCateId: opt._type === "treatCate" ? opt.id : null,
        itemId: opt._type === "item" ? opt.id : null,
      }));
      this.emitValue();
    },
    removeItem(idx) {
      this.selectedItems.splice(idx, 1);
      this.syncMultiselectFromItems();
      this.emitValue();
    },
    syncMultiselectFromItems() {
      this.selected = this.selectedItems
        .map(s => {
          if (s.treatCateId) {
            const cate = (this.getTreatCateList || []).find(
              c => c.id === s.treatCateId
            );
            return cate ? { ...cate, _type: "treatCate" } : null;
          }
          if (s.treatId) {
            const treat = (this.getTreatList || []).find(
              t => t.id === s.treatId
            );
            return treat ? { ...treat, _type: "treat" } : null;
          }
          if (s.itemId) {
            const item = (this.getItemList || []).find(
              i => i.id === s.itemId
            );
            return item ? { ...item, _type: "item" } : null;
          }
          return null;
        })
        .filter(Boolean);
    },
    emitValue() {
      const treatments = this.selectedItems.map(s => ({
        treatId: s.treatId || null,
        treatCateId: s.treatCateId || null,
        itemId: s.itemId || null,
      }));
      this.$emit("input", treatments);
    },
    showBrowseModal() {
      this.$refs.browseModal.show();
    },
    initBrowseSelection() {
      this.browseFilter = "";
      this.browseSelected = this.selectedItems.map(s => {
        if (s.treatCateId) {
          const c = (this.getTreatCateList || []).find(
            i => i.id === s.treatCateId
          );
          return {
            type: "treatCate",
            id: s.treatCateId,
            nameTh: c?.nameTh || "",
          };
        }
        if (s.treatId) {
          const t = (this.getTreatList || []).find(i => i.id === s.treatId);
          return {
            type: "treat",
            id: s.treatId,
            nameTh: t?.nameTh || "",
            code: t?.code || "",
          };
        }
        if (s.itemId) {
          const i = (this.getItemList || []).find(x => x.id === s.itemId);
          return {
            type: "item",
            id: s.itemId,
            nameTh: i?.nameTh || "",
            code: i?.code || "",
          };
        }
        return null;
      }).filter(Boolean);
    },
    applyBrowseSelection() {
      this.selectedItems = this.browseSelected.map(s => ({
        treatId: s.type === "treat" ? s.id : null,
        treatCateId: s.type === "treatCate" ? s.id : null,
        itemId: s.type === "item" ? s.id : null,
      }));
      this.syncMultiselectFromItems();
      this.emitValue();
    },
    selectAllTreatments() {
      const all = [];
      this.filteredTreatCateList.forEach(item => {
        all.push({ type: "treatCate", id: item.id, nameTh: item.nameTh });
      });
      this.filteredTreatList.forEach(item => {
        all.push({ type: "treat", id: item.id, nameTh: item.nameTh, code: item.code });
      });
      this.filteredItemList.forEach(item => {
        all.push({ type: "item", id: item.id, nameTh: item.nameTh, code: item.code });
      });
      this.browseSelected = all;
    },
    deselectAllTreatments() {
      this.browseSelected = [];
    },
    selectCategory(type) {
      const otherTypes = this.browseSelected.filter(s => s.type !== type);
      let additions = [];
      if (type === "treatCate") {
        additions = this.filteredTreatCateList.map(item => ({
          type: "treatCate", id: item.id, nameTh: item.nameTh,
        }));
      } else if (type === "treat") {
        additions = this.filteredTreatList.map(item => ({
          type: "treat", id: item.id, nameTh: item.nameTh, code: item.code,
        }));
      } else if (type === "item") {
        additions = this.filteredItemList.map(item => ({
          type: "item", id: item.id, nameTh: item.nameTh, code: item.code,
        }));
      }
      this.browseSelected = [...otherTypes, ...additions];
    },
    deselectCategory(type) {
      this.browseSelected = this.browseSelected.filter(s => s.type !== type);
    },
  },
};
</script>

<style scoped>
.picker-search {
  position: relative;
}
.picker-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.85rem;
  z-index: 1;
}
.picker-search-input {
  padding-left: 2.2rem;
  border-radius: 8px;
}
.btn-picker {
  border-radius: 6px;
  font-size: 0.78rem;
  padding: 0.25rem 0.6rem;
}
.picker-list-container {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
}
.picker-category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
}
.picker-category-section + .picker-category-section {
  border-top: 1px solid #e2e8f0;
}
.header-purple {
  background: linear-gradient(135deg, #f3e8ff, #ede5ff);
  color: #6b21a8;
}
.header-blue {
  background: linear-gradient(135deg, #e8f4fd, #dbeafe);
  color: #1e60a0;
}
.header-green {
  background: linear-gradient(135deg, #e6f9ed, #d1fae5);
  color: #166534;
}
.picker-category-actions {
  display: flex;
  gap: 0.5rem;
}
.picker-action-link {
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}
.picker-items {
  padding: 0.35rem 0.35rem 0.35rem 0;
}
.picker-item {
  padding: 0.35rem 0.75rem;
  padding-left: 2rem;
  margin: 0;
  transition: background 0.15s;
  font-size: 0.88rem;
}
.picker-item:hover {
  background: #eef4fb;
}
.picker-item >>> .custom-control-label::before,
.picker-item >>> .custom-control-label::after {
  left: -1.4rem;
}
.picker-code {
  font-size: 0.72rem;
  background: #e9ecef;
  color: #495057;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
}
.text-purple {
  color: #6b21a8 !important;
}
</style>
